/**
 * Client-side hand-off of a selected rate quote from the rate center to the
 * application flow.
 *
 * The full quote context is too large (and too easy to tamper with) for URL
 * search params, so we stash it in sessionStorage and carry an opaque token in
 * the URL. Nothing sensitive is stored — no borrower PII, no lender identity.
 * The internal investor/product mapping lives server-side, keyed by
 * `quoteToken`.
 */

import { describeFee, type LenderFeeEstimate } from "./lender-fee-rules";
import type { PrimaryLenderFee } from "./primary-lender-fee";

export type SelectedRateQuote = {
  /** Opaque server-issued token used to resolve internal lender metadata. */
  quoteToken: string | null;
  lenderLabel: string;
  programLabel: string;
  rate: number;
  apr: number;
  price: number | null;
  pricingBasis?: "par" | "points" | "credit" | "unknown";
  pricingLabel?: string;
  /** The primary lender fee amount only — never a combined total. */
  totalUpfrontLenderCharges?: number | null;
  /** Full primary-fee treatment shown to the borrower, for auditability. */
  primaryLenderFee?: PrimaryLenderFee | null;
  originationCharge?: number | null;
  lenderFeeLines?: Array<{ label: string; amount: number }>;
  /**
   * Full audit snapshot of the lender fee rules that were shown to the client
   * for this quote, so we can reproduce the disclosure later even if the
   * underlying fee schedule changes.
   */
  lenderFeeEstimate?: LenderFeeEstimate | null;
  /** Closing costs returned by the pricing engine for this quote, when available. */
  totalClosingCosts?: number | null;
  feesIncludedInPrice?: boolean | null;
  pointsPercent: number | null;
  pointsDollar: number | null;
  creditPercent: number | null;
  creditDollar: number | null;
  principalAndInterest: number | null;
  paymentBasis: "principal_and_interest" | "interest_only" | null;
  lockPeriod: number | null;
  prepaymentPenaltyLabel: string | null;
  interestOnly: boolean | null;
  /** Scenario the quote was priced on. */
  loanPurpose: string;
  state: string;
  city: string;
  county: string;
  zipCode: string;
  propertyValue: number;
  loanAmount: number;
  ltv: number;
  fico: number;
  dscr: number;
  propertyType: string;
  units: number;
  loanTerm: string;
  amortizationType: string;
  shortTermRental: boolean;
  entityVesting: boolean;
  citizenship: string;
  hasITIN: boolean;
  prepaymentPenalty: string;
  correlationId: string | null;
  /** Rate sheet timestamp for the quote. */
  asOf: string | null;
  /** When the borrower clicked. */
  selectedAt: string;
};

const KEY = "sam.selectedRateQuote";
/** Stale selections are ignored — pricing moves intraday. */
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

export function storeSelectedRate(q: SelectedRateQuote): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(q));
  } catch {
    // storage disabled — the lead still submits, just without quote context
  }
}

export function loadSelectedRate(): SelectedRateQuote | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SelectedRateQuote;
    if (!parsed?.selectedAt) return null;
    if (Date.now() - new Date(parsed.selectedAt).getTime() > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearSelectedRate(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

/**
 * Flattens the selected quote into the lead's calculator snapshot. Keys are
 * prefixed so staff can read them at a glance in the CRM and in the internal
 * notification email.
 */
export function selectedRateSnapshot(q: SelectedRateQuote): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {
    selected_rate_token: q.quoteToken ?? "",
    selected_rate: q.rate,
    selected_rate_apr: q.apr,
    selected_rate_lender_alias: q.lenderLabel,
    selected_rate_program: q.programLabel,
    selected_rate_loan_purpose: q.loanPurpose,
    selected_rate_state: q.state,
    selected_rate_zip: q.zipCode,
    selected_rate_property_value: q.propertyValue,
    selected_rate_loan_amount: q.loanAmount,
    selected_rate_ltv: q.ltv,
    selected_rate_fico: q.fico,
    selected_rate_dscr: q.dscr,
    selected_rate_property_type: q.propertyType,
    selected_rate_units: q.units,
    selected_rate_term: q.loanTerm,
    selected_rate_amortization: q.amortizationType,
    selected_rate_interest_only: q.interestOnly === true,
    selected_rate_prepayment: q.prepaymentPenalty,
    selected_rate_citizenship: q.citizenship,
    selected_rate_itin: q.hasITIN,
    selected_rate_entity_vesting: q.entityVesting,
    selected_rate_short_term_rental: q.shortTermRental,
    selected_rate_selected_at: q.selectedAt,
  };
  if (q.price != null) out.selected_rate_price = q.price;
  if (q.pricingBasis) out.selected_rate_pricing_basis = q.pricingBasis;
  if (q.pricingLabel) out.selected_rate_pricing_label = q.pricingLabel;
  if (q.totalUpfrontLenderCharges != null) {
    out.selected_rate_primary_lender_fee = q.totalUpfrontLenderCharges;
  }
  if (q.primaryLenderFee) {
    const p = q.primaryLenderFee;
    out.selected_rate_primary_fee_label = p.label;
    out.selected_rate_primary_fee_reflected_in_pricing = p.reflectedInPricing;
    out.selected_rate_primary_fee_match_status = p.matchStatus;
    if (p.feesInBasis) out.selected_rate_fees_in_basis = p.feesInBasis;
    if (p.varianceDollars != null) out.selected_rate_primary_fee_variance = p.varianceDollars;
    if (p.obReferenceAmount != null) {
      out.selected_rate_primary_fee_engine_reference = p.obReferenceAmount;
    }
    out.selected_rate_primary_fee_review_flag = p.reviewFlag;
  }
  if (q.feesIncludedInPrice != null) out.selected_rate_fees_in_price = q.feesIncludedInPrice;
  if (q.originationCharge != null) out.selected_rate_origination_charge = q.originationCharge;
  if (q.lenderFeeLines?.length) {
    out.selected_rate_lender_fees = q.lenderFeeLines
      .map((f) => `${f.label}: $${Math.round(f.amount).toLocaleString("en-US")}`)
      .join("; ");
  }
  const est = q.lenderFeeEstimate;
  if (est) {
    out.selected_rate_fee_schedule_found = est.scheduleFound;
    if (est.lenderKey) out.selected_rate_fee_lender_key = est.lenderKey;
    if (est.effectiveDate) out.selected_rate_fee_schedule_effective = est.effectiveDate;
    out.selected_rate_fee_program_family = est.programFamily;
    if (est.fixedTotal != null) out.selected_rate_fee_schedule_total = est.fixedTotal;
    if (est.fixedFees.length) {
      out.selected_rate_fee_rules_applied = est.fixedFees.map((f) => f.id).join(", ");
      // Full auditable line: official fee name, amount, why it applied and the
      // schedule date the borrower was shown.
      out.selected_rate_fee_rule_detail = est.fixedFees
        .map(
          (f) =>
            `${f.label} | ${describeFee(f)} | ${f.dscrApplicability}${
              f.conditionLabel ? ` | trigger: ${f.conditionLabel}` : ""
            }${f.effectiveDate ? ` | schedule ${f.effectiveDate}` : ""}`,
        )
        .join(" || ");
    }
    if (est.conditionalFees.length || est.variableCosts.length) {
      out.selected_rate_conditional_fees = [...est.conditionalFees, ...est.variableCosts]
        .map(describeFee)
        .join("; ");
    }
    if (est.excludedFees?.length) {
      out.selected_rate_excluded_fees = est.excludedFees
        .map((f) => `${f.officialName}: ${f.reason}`)
        .join(" || ");
    }
    if (est.verificationNotes.length) {
      out.selected_rate_fee_verification_notes = est.verificationNotes.join(" ");
    }
  }

  if (q.totalClosingCosts != null) out.selected_rate_total_closing_costs = q.totalClosingCosts;
  if (q.pointsPercent != null) out.selected_rate_points_percent = q.pointsPercent;
  if (q.pointsDollar != null) out.selected_rate_points_dollar = q.pointsDollar;
  if (q.creditPercent != null) out.selected_rate_credit_percent = q.creditPercent;
  if (q.creditDollar != null) out.selected_rate_credit_dollar = q.creditDollar;
  if (q.principalAndInterest != null) out.selected_rate_pi = q.principalAndInterest;
  if (q.paymentBasis) out.selected_rate_payment_basis = q.paymentBasis;
  if (q.lockPeriod != null) out.selected_rate_lock_days = q.lockPeriod;
  if (q.prepaymentPenaltyLabel) out.selected_rate_prepay_label = q.prepaymentPenaltyLabel;
  if (q.correlationId) out.selected_rate_quote_reference = q.correlationId;
  if (q.asOf) out.selected_rate_as_of = q.asOf;
  return out;
}
